import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { TasksContent } from "@/components/tasks/TasksContent";
import { TasksFilterWrapper } from "@/components/tasks/TasksFilterWrapper";
import { TasksActions } from "@/components/tasks/TasksActions";
import { CreateTaskForm } from "@/components/tasks/CreateTaskForm";
import { EditTaskModal } from "@/components/tasks/EditTaskModal";
import { Task } from "@/hooks/tasks/useTasks";
import { useJobTasks } from "@/hooks/tasks/useJobTasks";
import { TasksFilterProvider } from "@/contexts/TasksFilterContext";
import { usePersistedViewMode } from "@/utils/viewMode";
import { CheckSquare2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { usePageTitle } from "@/hooks/usePageTitle";

interface JobTasksTabProps {
  jobId: string;
  jobTitle?: string;
  companyId?: string;
  companyName?: string;
}

export const JobTasksTab = ({ jobId, jobTitle, companyId, companyName }: JobTasksTabProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [viewMode, setViewMode] = usePersistedViewMode("job-tasks", "list");
  
  const { data: tasks = [], isLoading, error, refetch } = useJobTasks(jobId);

  // Update page title based on selected task
  usePageTitle(
    selectedTask ? `${selectedTask.name} - ${jobTitle || 'Job'}` : jobTitle || 'Job'
  );

  // Handle URL parameter for opening task
  useEffect(() => {
    const taskId = searchParams.get('task');
    if (taskId) {
      const task = tasks.find(t => t.id === taskId);
      if (task && (!selectedTask || selectedTask.id !== taskId)) {
        setSelectedTask(task);
      } else if (!task && !isLoading) {
        supabase
          .from('tasks')
          .select('*, assignee:users!tasks_assigned_to_fkey(id, first_name, last_name), owner:users!tasks_user_id_fkey(id, first_name, last_name)')
          .eq('id', taskId)
          .single()
          .then(({ data, error }) => {
            if (data && !error && (!selectedTask || selectedTask.id !== taskId)) {
              setSelectedTask(data);
            }
          });
      }
    } else if (!taskId && selectedTask) {
      setSelectedTask(null);
    }
  }, [searchParams, tasks, isLoading]);

  // Handle closing task and updating URL
  const handleCloseTask = () => {
    setSelectedTask(null);
    // Remove task parameter from URL
    const currentParams = Object.fromEntries(searchParams.entries());
    delete currentParams.task;
    setSearchParams(currentParams, { replace: true });
  };

  if (error) {
    return (
      <div className="text-center">
        <h2 className="text-xl font-semibold text-red-600 mb-2">
          {t("common.error", "Error")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t("tasks.error.loading", "Failed to load tasks. Please try again.")}
        </p>
      </div>
    );
  }

  return (
    <TasksFilterProvider viewMode={viewMode} setViewMode={setViewMode}>
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t("jobs.tabs.tasks", "Tasks")}</h2>
            <p className="text-gray-500 dark:text-gray-400">{t("jobs.tabs.tasksDescription", "All tasks linked to this job")}</p>
          </div>
          <TasksActions
            onCreateTask={() => setIsCreateDialogOpen(true)}
          />
        </div>
        
        <TasksFilterWrapper />
        
        <TasksContent tasks={tasks} isLoading={isLoading} onTaskUpdated={refetch} />
        
        <CreateTaskForm
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          onSubmit={() => {
            setIsCreateDialogOpen(false);
            refetch();
          }}
          initialData={{
            relations: {
              candidates: [],
              matches: [],
              jobs: [jobId],
              companies: companyId ? [companyId] : [],
              calendar: []
            },
            // Pass job and company info for display
            _prelinkedJob: {
              id: jobId,
              title: jobTitle
            },
            ...(companyId && companyName && {
              _prelinkedCompany: {
                id: companyId,
                name: companyName
              }
            })
          }}
        />

        {selectedTask && (
          <EditTaskModal
            task={selectedTask}
            isOpen={true}
            onClose={handleCloseTask}
            onSave={() => {
              handleCloseTask();
              refetch();
            }}
          />
        )}
      </div>
    </TasksFilterProvider>
  );
};
